import { useState } from "react";
import { useRouter } from "next/router";
import styled from "styled-components";
import Link from "next/link";
import { useUser } from "@auth0/nextjs-auth0/client";
import {
  reference,
  spacing,
  text,
  label as labelFont,
  heading,
  body,
  background,
  border,
} from "@carvana/helios-styles";
import { useFlags } from "launchdarkly-react-client-sdk";
import useSegmentTrack from "@/hooks/useSegmentTrack";
import { useModal } from "@carvana/helios-layout";
import { Button } from "@carvana/helios-navigation";

import {
  flexAlignHorizontalContent,
  flexAlignVerticalContent,
} from "@/styles/mixins";

import { AuctionLogo } from "../icons";
import UserMenu from "./UserMenu";
import BetaBadge from "./HeaderBetaBadge";
import useSignOut from "@/hooks/useSignOut";

const AuthHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { adesaClearSellerPortal } = useFlags();
  const doSegmentTrack = useSegmentTrack();
  const { Modal, openModal, closeModal } = useModal();
  const { user } = useUser();
  const { pathname } = useRouter();
  const { signOut } = useSignOut();
  const browseHref = "/browse";

  const getButtonClassName = (segment: string) =>
    pathname.includes(segment) ? "nav--active" : "nav--inactive";

  const navLinks: Array<{
    label: string;
    className: string;
    href: string;
  }> = [
    {
      label: "Buy",
      className: getButtonClassName("browse"),
      href: browseHref,
    },
    {
      label: "Purchases",
      className: getButtonClassName("purchases"),
      href: "/purchases",
    },
  ];

  if (adesaClearSellerPortal === true) {
    navLinks.push({
      label: "Sell",
      className: getButtonClassName("sell"),
      href: "/sell",
    });
  }

  const toggleUserMenu = () => setIsMenuOpen(!isMenuOpen);

  const onSignout = () => {
    openModal();
    doSegmentTrack("Sign Out Initiated");
  };

  const closeSignOutModal = () => {
    doSegmentTrack("Sign Out Cancelled");
    closeModal();
  };

  return (
    <>
      <HeaderContainer onClick={() => isMenuOpen && toggleUserMenu()}>
        <Left>
          <Logo>
            <Link href={browseHref}>
              <AuctionLogo />
            </Link>
            <BetaBadge />
          </Logo>
          <Nav>
            {navLinks.map(({ label, ...rest }) => (
              <NavLink
                data-qa={`nav-${label.toLowerCase()}`}
                key={label}
                {...rest}
              >
                {label}
              </NavLink>
            ))}
          </Nav>
        </Left>
        <Icons>
          <MyBidsLink data-qa="nav-my-bids" href="/mybids">
            My Bids
          </MyBidsLink>
          <Divider />
          <UserMenu
            name={user?.name ?? ""}
            email={user?.email ?? ""}
            isOpen={isMenuOpen}
            onClick={toggleUserMenu}
            onSignout={onSignout}
          />
        </Icons>
      </HeaderContainer>
      {isMenuOpen && <Backdrop onClick={toggleUserMenu} />}
      <SignOutConfirmationModal
        Modal={Modal}
        closeModal={closeSignOutModal}
        handleSignout={signOut}
      />
    </>
  );
};

type SignOutConfirmationModalProps = {
  Modal: ReturnType<typeof useModal>["Modal"];
  closeModal: () => void;
  handleSignout: () => void;
};

const SignOutConfirmationModal = ({
  Modal,
  closeModal,
  handleSignout,
}: SignOutConfirmationModalProps) => {
  return (
    <Modal data-qa="signout-modal">
      <Body>
        <Header>Sign out</Header>
        <p>Are you sure you want to sign out?</p>
      </Body>
      <Footer>
        <CancelBtn
          variant="primaryInverted"
          utility
          onClick={closeModal}
          data-qa="signout-cancell-btn"
        >
          <CancelBtnText>Cancel</CancelBtnText>
        </CancelBtn>
        <SignOutBtn
          variant="primary"
          utility
          onClick={handleSignout}
          data-qa="signout-confirm-btn"
        >
          <SignOutBtnText>Sign out</SignOutBtnText>
        </SignOutBtn>
      </Footer>
    </Modal>
  );
};

const HeaderContainer = styled.header`
  ${flexAlignHorizontalContent("normal", "center")}
  gap: ${spacing.xl};
  height: var(--header-height);
  background-color: ${background.default};
  border-bottom: 1px solid ${border.subtle};
  position: sticky;
  top: 0;
  padding: ${spacing.xl} ${spacing["2Xl"]};
  z-index: 300;
`;

const Left = styled.div`
  ${flexAlignHorizontalContent("center", "center")}
  gap: ${spacing["3Xl"]};
`;

const Logo = styled.div`
  ${flexAlignHorizontalContent("center", "center")}
  gap: 5px;
`;

const Icons = styled.div`
  ${flexAlignHorizontalContent("flex-end", "center")}
  gap: ${spacing["2Xl"]};
  flex: 1 0 0;
`;

const Nav = styled.nav`
  display: flex;
`;

const NavLink = styled(Link)`
  ${labelFont.sm}
  padding: ${spacing.lg} ${spacing.xl};
  &.nav--inactive {
    color: ${text.weak};
  }
  &.nav--active {
    color: ${text.primaryActive};
  }
  :hover {
    color: ${text.primaryHover};
  }
`;

const MyBidsLink = styled(Link)`
  ${labelFont.md}
  color: ${text.primary};
  :hover {
    color: ${text.primaryHover};
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 40px;
  background-color: ${border.subtle};
`;

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${reference.slate[700]};
  opacity: 0.6;
  z-index: 200;
`;

const Header = styled.div`
  ${flexAlignVerticalContent("center", "center")}
  ${heading.lg}
  color: ${text.strong};
  margin-bottom: 20px;
`;

const Body = styled.span`
  ${flexAlignVerticalContent("center", "center")}
  ${body.regular.md}
  color: ${text.weak};
  margin-bottom: 25px;
`;

const Footer = styled.div`
  ${flexAlignHorizontalContent("flex-end", "center")}
  gap: 10px;
`;

const CancelBtn = styled(Button)`
  ${background.weakTransparent}
`;

const CancelBtnText = styled.p`
  ${labelFont.sm}
  color: ${text.primary};
`;

const SignOutBtn = styled(Button)`
  ${background.primary}
`;

const SignOutBtnText = styled.p`
  ${labelFont.sm}
  color: ${text.inverse};
`;

export default AuthHeader;
